/**
 * SettingsPage — profile editing and notification preferences.
 *
 * Sections:
 *  1. Birth details form (name, DOB, time, place, lat/lon, timezone)
 *     → PATCH /v1/users/profile via profileApi.update()
 *  2. Notification preferences
 *     → GET/PATCH /v1/notifications/preferences via notificationsApi
 *     → Browser permission request via useNotifications hook
 *  3. Account info (read-only)
 */
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { profileApi, notificationsApi, type SetupProfilePayload, type NotificationPreferences } from '@/lib/api'
import { useAppStore, useUser } from '@/store'
import { useNotifications } from '@/hooks/useNotifications'
import { useSubscription } from '@/hooks/useSubscription'
import { cn } from '@/lib/utils'
import { Save, User, Bell, CheckCircle, AlertCircle, RefreshCw, ShieldAlert, BellOff, BellRing, CreditCard, Sun, Moon, Palette } from 'lucide-react'
import { ThemeToggle } from '@/components/ThemeToggle'
import { useTheme } from '@/store'

// ── Reusable field components ──────────────────────────────────────────────────

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="block text-star-dim text-2xs font-mono uppercase tracking-wider mb-1.5">
      {children}
    </label>
  )
}

function TextInput({
  id, value, onChange, placeholder, type = 'text', disabled,
}: {
  id: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  type?: string
  disabled?: boolean
}) {
  return (
    <input
      id={id}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="input-cosmic w-full"
    />
  )
}

function CoordInput({
  id, label, value, onChange, disabled,
}: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  disabled?: boolean
}) {
  const isValid = value === '' || (!isNaN(Number(value)) && isFinite(Number(value)))
  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <input
        id={id}
        type="number"
        step="0.0001"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={cn('input-cosmic w-full', !isValid && 'border-inauspicious/50 focus:ring-inauspicious/30')}
      />
    </div>
  )
}

// ── Section wrapper ───────────────────────────────────────────────────────────

function Section({ title, sanskrit, icon: Icon, children }: {
  title: string
  sanskrit: string
  icon: React.ElementType
  children: React.ReactNode
}) {
  return (
    <div className="card-cosmic p-6 space-y-5">
      <div className="flex items-center gap-3 pb-4 border-b border-cosmic-mist">
        <div className="w-8 h-8 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-gold" />
        </div>
        <div>
          <h2 className="text-star-bright text-sm font-semibold">{title}</h2>
          <p className="text-star-dim text-2xs font-display font-mono">{sanskrit}</p>
        </div>
      </div>
      {children}
    </div>
  )
}

// ── Notification toggle row ───────────────────────────────────────────────────

function NotifToggle({ label, description, checked, onChange, disabled }: {
  label: string
  description: string
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
}) {
  const id = label.toLowerCase().replace(/\s+/g, '-')
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <p className="text-star-soft text-sm font-medium">{label}</p>
        <p className="text-star-dim text-2xs mt-0.5">{description}</p>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        aria-label={label}
        id={id}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex w-10 h-5.5 rounded-full border transition-colors flex-shrink-0 mt-0.5',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          checked
            ? 'bg-gold/20 border-gold/40'
            : 'bg-cosmic-dusk border-cosmic-mist',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 w-4 h-4 rounded-full transition-transform',
            checked ? 'translate-x-4.5 bg-gold' : 'translate-x-0 bg-star-dim',
          )}
        />
      </button>
    </div>
  )
}

// ── Time input ────────────────────────────────────────────────────────────────

function TimeInput({ id, label, value, onChange, disabled }: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  disabled?: boolean
}) {
  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <input
        id={id}
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="input-cosmic w-full [color-scheme:dark]"
      />
    </div>
  )
}

// ── Permission status banner ──────────────────────────────────────────────────

function PermissionBanner({ onRequest, requesting }: {
  onRequest: () => void
  requesting: boolean
}) {
  const { permissionState, isSupported } = useNotifications()

  if (!isSupported) {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg bg-cosmic-dusk border border-cosmic-mist">
        <BellOff className="w-4 h-4 text-star-dim flex-shrink-0" />
        <p className="text-star-dim text-2xs">
          Push notifications are not supported in this browser.
        </p>
      </div>
    )
  }

  if (permissionState === 'denied') {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg bg-inauspicious/10 border border-inauspicious/30">
        <ShieldAlert className="w-4 h-4 text-inauspicious flex-shrink-0" />
        <p className="text-inauspicious text-2xs">
          Notifications are blocked. To re-enable, update your browser's site settings for this page.
        </p>
      </div>
    )
  }

  if (permissionState === 'granted') {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg bg-auspicious/10 border border-auspicious/30">
        <BellRing className="w-4 h-4 text-auspicious flex-shrink-0" />
        <p className="text-auspicious text-2xs">
          Push notifications are active.
        </p>
      </div>
    )
  }

  // 'default' — not yet requested
  return (
    <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-cosmic-dusk border border-gold/20">
      <div className="flex items-center gap-3">
        <Bell className="w-4 h-4 text-gold flex-shrink-0" />
        <p className="text-star-soft text-2xs">
          Enable push notifications to receive timings and alerts.
        </p>
      </div>
      <button
        onClick={onRequest}
        disabled={requesting}
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-2xs font-semibold flex-shrink-0',
          'bg-gold/20 border border-gold/30 text-gold',
          'hover:bg-gold/30 transition-colors',
          'disabled:opacity-60 disabled:cursor-not-allowed',
        )}
      >
        {requesting
          ? <span className="w-3 h-3 rounded-full border border-gold border-t-transparent animate-spin" />
          : <Bell className="w-3 h-3" />
        }
        {requesting ? 'Requesting…' : 'Enable'}
      </button>
    </div>
  )
}

// ── Default preferences ────────────────────────────────────────────────────────

const DEFAULT_PREFS: NotificationPreferences = {
  morningSummary:     true,
  morningSummaryTime: '06:30',
  rahuKaalAlert:      true,
  abhijitMuhurta:     false,
  nakshatraChange:    false,
  quietHoursEnabled:  false,
  quietHoursStart:    '22:00',
  quietHoursEnd:      '07:00',
}

// ── Notifications section ─────────────────────────────────────────────────────

function NotificationsSection() {
  const qc = useQueryClient()
  const { permissionState, requestPermission } = useNotifications()
  const [requesting, setRequesting] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle')

  // Fetch preferences from backend
  const { data: prefs, isLoading } = useQuery({
    queryKey: ['notif-prefs'],
    queryFn: () => notificationsApi.getPreferences(),
    // Fall back to defaults if backend not yet wired
    placeholderData: DEFAULT_PREFS,
  })

  const [local, setLocal] = useState<NotificationPreferences>(prefs ?? DEFAULT_PREFS)

  // Sync when remote data arrives
  useEffect(() => {
    if (prefs) setLocal(prefs)
  }, [prefs])

  const saveMutation = useMutation({
    mutationFn: (p: Partial<NotificationPreferences>) => notificationsApi.updatePreferences(p),
    onSuccess: (updated) => {
      qc.setQueryData(['notif-prefs'], updated)
      setSaveStatus('saved')
      setTimeout(() => setSaveStatus('idle'), 3000)
    },
    onError: () => {
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 4000)
    },
  })

  function set<K extends keyof NotificationPreferences>(key: K, value: NotificationPreferences[K]) {
    setLocal((p) => ({ ...p, [key]: value }))
  }

  function handleSave() {
    saveMutation.mutate(local)
  }

  async function handleRequestPermission() {
    setRequesting(true)
    await requestPermission()
    setRequesting(false)
  }

  const prefsDeniedOrUnsupported = permissionState === 'denied' || permissionState === 'unsupported'
  const isSaving = saveMutation.isPending

  return (
    <Section title="Notifications" sanskrit="सूचनाएं" icon={Bell}>
      <div className="space-y-5">

        {/* Permission status */}
        <PermissionBanner onRequest={handleRequestPermission} requesting={requesting} />

        {/* Toggles */}
        <div className={cn('space-y-4', prefsDeniedOrUnsupported && 'opacity-50 pointer-events-none')}>

          {/* Morning summary */}
          <NotifToggle
            label="Morning Summary"
            description="Daily Panchang push with tithi, nakshatra, and auspiciousness score"
            checked={local.morningSummary}
            onChange={(v) => set('morningSummary', v)}
            disabled={isLoading}
          />

          {/* Morning time picker — shown only when morning summary is on */}
          {local.morningSummary && (
            <div className="ml-0 pl-4 border-l-2 border-gold/20">
              <div className="max-w-[140px]">
                <TimeInput
                  id="morning-time"
                  label="Delivery time"
                  value={local.morningSummaryTime}
                  onChange={(v) => set('morningSummaryTime', v)}
                  disabled={isLoading}
                />
              </div>
            </div>
          )}

          <div className="border-t border-cosmic-mist" />

          <NotifToggle
            label="Rahu Kaal Alert"
            description="Notify 15 min before Rahu Kaal starts — avoid important tasks"
            checked={local.rahuKaalAlert}
            onChange={(v) => set('rahuKaalAlert', v)}
            disabled={isLoading}
          />

          <div className="border-t border-cosmic-mist" />

          <NotifToggle
            label="Abhijit Muhurta"
            description="Alert when the highly auspicious Abhijit window is approaching"
            checked={local.abhijitMuhurta}
            onChange={(v) => set('abhijitMuhurta', v)}
            disabled={isLoading}
          />

          <div className="border-t border-cosmic-mist" />

          <NotifToggle
            label="Nakshatra Change"
            description="Notify when the Moon moves into a new Nakshatra"
            checked={local.nakshatraChange}
            onChange={(v) => set('nakshatraChange', v)}
            disabled={isLoading}
          />

          <div className="border-t border-cosmic-mist" />

          {/* Quiet hours */}
          <NotifToggle
            label="Quiet Hours"
            description="Suppress all notifications during a set time window"
            checked={local.quietHoursEnabled}
            onChange={(v) => set('quietHoursEnabled', v)}
            disabled={isLoading}
          />

          {local.quietHoursEnabled && (
            <div className="ml-0 pl-4 border-l-2 border-cosmic-mist">
              <div className="grid grid-cols-2 gap-4 max-w-[300px]">
                <TimeInput
                  id="quiet-start"
                  label="Start"
                  value={local.quietHoursStart}
                  onChange={(v) => set('quietHoursStart', v)}
                  disabled={isLoading}
                />
                <TimeInput
                  id="quiet-end"
                  label="End"
                  value={local.quietHoursEnd}
                  onChange={(v) => set('quietHoursEnd', v)}
                  disabled={isLoading}
                />
              </div>
              <p className="text-star-dim text-2xs mt-2">
                No notifications will be sent during this period.
              </p>
            </div>
          )}
        </div>

        {/* Save row */}
        <div className="flex items-center gap-3 pt-1">
          <button
            onClick={handleSave}
            disabled={isSaving || prefsDeniedOrUnsupported}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              'bg-gold/20 border border-gold/30 text-gold',
              'hover:bg-gold/30 disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            {isSaving
              ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              : <Save className="w-3.5 h-3.5" />
            }
            {isSaving ? 'Saving…' : 'Save Preferences'}
          </button>

          {saveStatus === 'saved' && (
            <span className="flex items-center gap-1.5 text-auspicious text-sm">
              <CheckCircle className="w-3.5 h-3.5" />
              Saved
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="flex items-center gap-1.5 text-inauspicious text-sm">
              <AlertCircle className="w-3.5 h-3.5" />
              Failed to save
            </span>
          )}
        </div>
      </div>
    </Section>
  )
}

// ── Appearance section ────────────────────────────────────────────────────────

function AppearanceSection() {
  const theme = useTheme()

  return (
    <Section title="Appearance" sanskrit="दर्शन" icon={Palette}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-star-soft text-sm font-medium">Theme</p>
          <p className="text-star-dim text-2xs mt-0.5">
            {theme === 'dark' ? 'Dark mode — cosmic deep space' : 'Light mode — soft starlight'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Sun className={cn('w-4 h-4 transition-colors', theme === 'light' ? 'text-gold' : 'text-star-dim')} aria-hidden="true" />
          <ThemeToggle className="w-10 h-10 rounded-xl border border-cosmic-mist hover:border-gold/30" />
          <Moon className={cn('w-4 h-4 transition-colors', theme === 'dark' ? 'text-violet-glow' : 'text-star-dim')} aria-hidden="true" />
        </div>
      </div>
    </Section>
  )
}

// ── Subscription section ──────────────────────────────────────────────────────

function SubscriptionSection() {
  const { subscription, isLoading } = useSubscription()

  if (isLoading || !subscription) return null

  const STATE_LABEL: Record<string, string> = {
    free:      'Free',
    trialing:  'Trial',
    active:    'Active',
    past_due:  'Past Due',
    cancelled: 'Cancelled',
  }
  const STATE_CLASS: Record<string, string> = {
    free:      'bg-star-dim/10 text-star-dim',
    trialing:  'bg-violet/15 text-violet-glow',
    active:    'bg-auspicious/15 text-auspicious',
    past_due:  'bg-inauspicious/15 text-inauspicious',
    cancelled: 'bg-star-dim/10 text-star-dim',
  }

  const trialDaysLeft =
    subscription.state === 'trialing' && subscription.trialEndsAt
      ? Math.max(0, Math.ceil((new Date(subscription.trialEndsAt).getTime() - Date.now()) / 86_400_000))
      : null

  return (
    <div className="card-cosmic p-5">
      <div className="flex items-center gap-3 pb-4 mb-4 border-b border-cosmic-mist">
        <div className="w-8 h-8 rounded-lg bg-violet/10 border border-violet/20 flex items-center justify-center flex-shrink-0">
          <CreditCard className="w-4 h-4 text-violet-glow" />
        </div>
        <div>
          <h2 className="text-star-bright text-sm font-semibold">Subscription</h2>
          <p className="text-star-dim text-2xs font-mono">सदस्यता</p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-star-white text-sm font-medium">
              {subscription.planId
                ? subscription.planId === 'premium_annual' ? 'Premium (Annual)' : 'Premium (Monthly)'
                : 'Free plan'}
            </span>
            <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-2xs font-mono font-medium', STATE_CLASS[subscription.state] ?? STATE_CLASS.free)}>
              {STATE_LABEL[subscription.state] ?? subscription.state}
            </span>
          </div>

          {trialDaysLeft !== null && (
            <p className={cn('text-2xs', trialDaysLeft <= 1 ? 'text-inauspicious' : 'text-star-dim')}>
              {trialDaysLeft === 0
                ? 'Trial ends today'
                : trialDaysLeft === 1
                ? '1 day remaining in trial'
                : `${trialDaysLeft} days remaining in trial`}
            </p>
          )}

          {subscription.state === 'free' && (
            <p className="text-star-dim text-2xs">
              3 Muhurta lookups/month · Basic features
            </p>
          )}
        </div>

        <Link
          to="/billing"
          className="shrink-0 px-3 py-1.5 rounded-lg border border-cosmic-mist text-star-soft text-2xs font-medium hover:border-violet-dark hover:text-star-bright transition-all"
        >
          Manage →
        </Link>
      </div>

      {subscription.state === 'free' && (
        <div className="mt-4 pt-4 border-t border-cosmic-mist">
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-violet to-violet-mid text-star-white text-xs font-medium hover:shadow-glow-violet transition-all"
          >
            Start free 7-day trial
          </Link>
          <p className="text-star-dim text-2xs mt-1.5">No credit card required.</p>
        </div>
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function SettingsPage() {
  const user = useUser()
  const setUser = useAppStore((s) => s.setUser)

  // ── Profile form state ─────────────────────────────────────────────────────
  const [name,        setName]        = useState(user?.displayName              ?? '')
  const [birthDate,   setBirthDate]   = useState(user?.profile?.dateOfBirth    ?? '')
  const [birthTime,   setBirthTime]   = useState(user?.profile?.timeOfBirth    ?? '')
  const [birthPlace,  setBirthPlace]  = useState(user?.profile?.birthCity      ?? '')
  const [lat,         setLat]         = useState('')
  const [lon,         setLon]         = useState('')
  const [timezone,    setTimezone]    = useState(user?.profile?.birthTimezone  ?? '')

  // Keep form in sync if user loads asynchronously
  useEffect(() => {
    if (user) {
      setName(user.displayName ?? '')
      setBirthDate(user.profile?.dateOfBirth  ?? '')
      setBirthTime(user.profile?.timeOfBirth  ?? '')
      setBirthPlace(user.profile?.birthCity   ?? '')
      setTimezone(user.profile?.birthTimezone ?? '')
    }
  }, [user?.id])

  // ── Save mutation ──────────────────────────────────────────────────────────
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle')

  const mutation = useMutation({
    mutationFn: (payload: Partial<SetupProfilePayload>) => profileApi.update(payload),
    onSuccess: (updatedUser) => {
      setUser(updatedUser)
      setSaveStatus('saved')
      setTimeout(() => setSaveStatus('idle'), 3000)
    },
    onError: () => {
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 4000)
    },
  })

  function handleSaveProfile(e: React.FormEvent) {
    e.preventDefault()

    const payload: Partial<SetupProfilePayload> = {
      displayName:   name.trim()       || undefined,
      dateOfBirth:   birthDate         || undefined,
      timeOfBirth:   birthTime         || undefined,
      birthCity:     birthPlace.trim() || undefined,
      birthLatitude:  lat  ? Number(lat)  : undefined,
      birthLongitude: lon  ? Number(lon)  : undefined,
      birthTimezone:  timezone.trim()   || undefined,
    }

    const cleaned = Object.fromEntries(
      Object.entries(payload).filter(([, v]) => v !== undefined),
    ) as Partial<SetupProfilePayload>

    mutation.mutate(cleaned)
  }

  const isSaving = mutation.isPending

  return (
    <div className="max-w-2xl space-y-6">

      {/* Page header */}
      <div>
        <h1 className="text-star-white text-xl font-semibold">Settings</h1>
        <p className="text-star-muted text-sm mt-0.5 font-mono">सेटिंग्स · Profile and preferences</p>
      </div>

      {/* Profile section */}
      <Section title="Birth Details" sanskrit="जन्म विवरण" icon={User}>
        <form onSubmit={handleSaveProfile} className="space-y-4">

          {/* Name */}
          <div>
            <FieldLabel htmlFor="name">Display Name</FieldLabel>
            <TextInput id="name" value={name} onChange={setName} placeholder="Your name" disabled={isSaving} />
          </div>

          {/* DOB + Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel htmlFor="birthDate">Birth Date</FieldLabel>
              <TextInput id="birthDate" type="date" value={birthDate} onChange={setBirthDate} disabled={isSaving} />
            </div>
            <div>
              <FieldLabel htmlFor="birthTime">Birth Time</FieldLabel>
              <TextInput id="birthTime" type="time" value={birthTime} onChange={setBirthTime} disabled={isSaving} />
            </div>
          </div>

          {/* Place */}
          <div>
            <FieldLabel htmlFor="birthPlace">Birth Place</FieldLabel>
            <TextInput id="birthPlace" value={birthPlace} onChange={setBirthPlace}
              placeholder="City, Country" disabled={isSaving} />
          </div>

          {/* Coordinates */}
          <div className="grid grid-cols-2 gap-4">
            <CoordInput id="lat" label="Latitude" value={lat} onChange={setLat} disabled={isSaving} />
            <CoordInput id="lon" label="Longitude" value={lon} onChange={setLon} disabled={isSaving} />
          </div>
          <p className="text-star-dim text-2xs -mt-2">
            Leave blank to keep current coordinates. Use decimal degrees (e.g. 28.6139 / 77.2090).
          </p>

          {/* Timezone */}
          <div>
            <FieldLabel htmlFor="timezone">Timezone</FieldLabel>
            <TextInput id="timezone" value={timezone} onChange={setTimezone}
              placeholder="Asia/Kolkata" disabled={isSaving} />
            <p className="text-star-dim text-2xs mt-1">IANA timezone string, e.g. Asia/Kolkata</p>
          </div>

          {/* Submit row */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={isSaving}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                'bg-gold/20 border border-gold/30 text-gold',
                'hover:bg-gold/30 disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              {isSaving
                ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                : <Save className="w-3.5 h-3.5" />
              }
              {isSaving ? 'Saving…' : 'Save Changes'}
            </button>

            {saveStatus === 'saved' && (
              <span className="flex items-center gap-1.5 text-auspicious text-sm">
                <CheckCircle className="w-3.5 h-3.5" />
                Saved
              </span>
            )}
            {saveStatus === 'error' && (
              <span className="flex items-center gap-1.5 text-inauspicious text-sm">
                <AlertCircle className="w-3.5 h-3.5" />
                Failed to save
              </span>
            )}
          </div>
        </form>
      </Section>

      {/* Notifications section */}
      <NotificationsSection />

      {/* Appearance section */}
      <AppearanceSection />

      {/* Subscription status */}
      <SubscriptionSection />

      {/* Account info (read-only) */}
      <div className="card-cosmic p-5">
        <h3 className="text-star-bright text-sm font-semibold mb-3">Account</h3>
        <div className="space-y-2">
          {[
            { label: 'Email',    value: user?.email    ?? '—' },
            { label: 'User ID',  value: user?.id       ?? '—' },
            { label: 'Joined',   value: user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }) : '—' },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-baseline justify-between gap-4 py-1.5 border-b border-cosmic-mist last:border-0">
              <span className="text-star-dim text-2xs font-mono uppercase tracking-wider">{label}</span>
              <span className="text-star-soft text-sm font-mono truncate max-w-xs text-right">{value}</span>
            </div>
          ))}
        </div>
      </div>

    </div>
  )
}
